/**
 * ポップアップ
 *
 * 内容ビルダーは props を受けて DOM を返すだけで editor を持たない。可変状態 (開いている 1 個) と
 * 外側クリック用の document リスナは createPopupController に閉じ込め、解放経路を destroy() 1 本にする
 *
 * クラス名は SCSS と Gauge の Web テストが参照する外部契約なので変更しない
 */
import { icon } from './icons';
import { shouldApplyLinkOnEnter, tableSizeLabel } from './state';
import type { RichTextAlignItem, RichTextSelectItem } from './config';

/** フォーカス移動を止める。ツールバー操作でエディタの選択範囲を失わないため */
const preventFocusSteal = (el: HTMLElement): void => {
  el.addEventListener('pointerdown', (e) => e.preventDefault());
};

/** ポップアップ内の 1 行ボタン (チェックマーク + 中身) */
const createPopupItem = (innerHtml: string, pressed: boolean, onClick: () => void): HTMLButtonElement => {
  const el = document.createElement('button');
  el.type = 'button';
  el.className = 'codmon-rte__popup-item';
  el.setAttribute('aria-pressed', pressed ? 'true' : 'false');
  el.innerHTML = `<span class="codmon-rte__check"></span>${innerHtml}`;
  preventFocusSteal(el);
  el.addEventListener('click', onClick);
  return el;
};

/**
 * 開いているポップアップを 1 個だけ保持するコントローラ
 * document の mousedown を capture で購読するので、使い終わったら必ず destroy() を呼ぶ
 */
export const createPopupController = ({
  toolbarEl,
}: {
  toolbarEl: HTMLElement;
}): {
  /** anchor の直下に配置して開く。すでに開いているものは閉じる */
  open(popup: HTMLElement, anchor: HTMLElement, className: string): void;
  close(): void;
  /** 指定クラスのポップアップが開いているか (トグル判定用) */
  isOpen(className: string): boolean;
  destroy(): void;
} => {
  let openPopup: HTMLElement | null = null;
  let openAnchor: HTMLElement | null = null;

  const close = (): void => {
    if (openPopup?.parentNode) openPopup.parentNode.removeChild(openPopup);
    openAnchor?.classList.remove('is-open');
    openPopup = null;
    openAnchor = null;
  };

  const onDocMouseDown = (e: MouseEvent): void => {
    const target = e.target as Node | null;
    if (!openPopup || !target) return;
    if (!openPopup.contains(target) && !toolbarEl.contains(target)) close();
  };
  document.addEventListener('mousedown', onDocMouseDown, true);

  return {
    open: (popup, anchor, className) => {
      close();
      popup.className = 'codmon-rte__popup' + (className ? ' ' + className : '');
      popup.style.left = anchor.offsetLeft + 'px';
      popup.style.top = anchor.offsetTop + anchor.offsetHeight + 4 + 'px';
      toolbarEl.appendChild(popup);
      openPopup = popup;
      openAnchor = anchor;
      anchor.classList.add('is-open');
    },
    close,
    isOpen: (className) => !!openPopup?.classList.contains(className),
    destroy: () => {
      document.removeEventListener('mousedown', onDocMouseDown, true);
      close();
    },
  };
};

/** SelectBox の選択肢一覧 (フォント / 段落 / 文字サイズ) */
export const createSelectPopup = ({
  items,
  currentValue,
  renderItem,
  onSelect,
}: {
  items: readonly RichTextSelectItem[];
  /** 空文字を渡すとどれも選択なし (未フォーカス時の中立表示) になる */
  currentValue: string;
  renderItem(item: RichTextSelectItem): string;
  onSelect(value: string): void;
}): HTMLElement => {
  const el = document.createElement('div');
  for (const item of items) {
    el.appendChild(createPopupItem(renderItem(item), item.value === currentValue, () => onSelect(item.value)));
  }
  return el;
};

/** 色ポップアップ。列ごとにパレットと「透明」を出す (背景色 / 文字色 の 2 列) */
export const createColorPopup = ({
  palette,
  columns,
}: {
  palette: readonly string[];
  columns: readonly {
    title: string;
    onPick(color: string): void;
    /** 「透明」ボタン。省略した列には出さない */
    onUnset?: () => void;
  }[];
}): HTMLElement => {
  const el = document.createElement('div');
  const columnsEl = document.createElement('div');
  columnsEl.className = 'codmon-rte__color-columns';
  for (const { title, onPick, onUnset } of columns) {
    const column = document.createElement('div');
    column.className = 'codmon-rte__color-column';
    column.innerHTML = `<div class="codmon-rte__color-title">${title}</div>`;
    const grid = document.createElement('div');
    grid.className = 'codmon-rte__color-grid';
    for (const color of palette) {
      const swatch = document.createElement('button');
      swatch.type = 'button';
      swatch.className = 'codmon-rte__swatch';
      swatch.style.backgroundColor = color;
      swatch.setAttribute('aria-label', color);
      preventFocusSteal(swatch);
      swatch.addEventListener('click', () => onPick(color));
      grid.appendChild(swatch);
    }
    column.appendChild(grid);
    if (onUnset) {
      const reset = document.createElement('button');
      reset.type = 'button';
      reset.className = 'codmon-rte__color-reset';
      reset.innerHTML = `<span class="codmon-rte__icon">${icon('colorReset')}</span><span>透明</span>`;
      preventFocusSteal(reset);
      reset.addEventListener('click', onUnset);
      column.appendChild(reset);
    }
    columnsEl.appendChild(column);
  }
  el.appendChild(columnsEl);
  return el;
};

/** 揃えとインデントのポップアップ。aria-pressed は揃え系の項目にだけ意味がある */
export const createAlignPopup = ({
  items,
  currentAlign,
  onSelect,
}: {
  items: readonly RichTextAlignItem[];
  currentAlign: string;
  onSelect(cmd: RichTextAlignItem['cmd']): void;
}): HTMLElement => {
  const el = document.createElement('div');
  el.className = '';
  const alignCommands = ['left', 'center', 'right', 'justify'];
  for (const item of items) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'codmon-rte__align-item';
    const isAlign = alignCommands.includes(item.cmd);
    button.setAttribute('aria-pressed', isAlign && item.cmd === currentAlign ? 'true' : 'false');
    button.setAttribute('aria-label', item.label);
    button.title = item.label;
    button.innerHTML = `<span class="codmon-rte__icon">${icon(item.icon)}</span>`;
    preventFocusSteal(button);
    button.addEventListener('click', () => onSelect(item.cmd));
    el.appendChild(button);
  }
  return el;
};

/** 行の高さのポップアップ */
export const createLineHeightPopup = ({
  values,
  current,
  onSelect,
}: {
  values: readonly string[];
  current: string | null;
  onSelect(value: string): void;
}): HTMLElement => {
  const el = document.createElement('div');
  for (const value of values) {
    el.appendChild(createPopupItem(`<span>${value}</span>`, current === value, () => onSelect(value)));
  }
  return el;
};

/** テーブル挿入のグリッドセレクタ。ホバーした範囲を光らせ、クリックでサイズを返す */
export const createTablePopup = ({
  max,
  onSelect,
}: {
  max: number;
  onSelect(rows: number, cols: number): void;
}): HTMLElement => {
  const el = document.createElement('div');
  const grid = document.createElement('div');
  grid.className = 'codmon-rte__table-grid';
  const label = document.createElement('div');
  label.className = 'codmon-rte__table-label';
  const cells: { el: HTMLElement; row: number; col: number }[] = [];

  const highlight = (hoverRow: number, hoverCol: number): void => {
    for (const cell of cells) {
      cell.el.classList.toggle('is-on', cell.row <= hoverRow && cell.col <= hoverCol);
    }
    label.textContent = tableSizeLabel(hoverRow, hoverCol);
  };

  for (let row = 0; row < max; row += 1) {
    for (let col = 0; col < max; col += 1) {
      const cell = document.createElement('button');
      cell.type = 'button';
      cell.className = 'codmon-rte__table-cell';
      preventFocusSteal(cell);
      cell.addEventListener('mouseenter', () => highlight(row, col));
      cell.addEventListener('click', () => onSelect(row + 1, col + 1));
      cells.push({ el: cell, row, col });
      grid.appendChild(cell);
    }
  }
  el.appendChild(grid);
  el.appendChild(label);
  // summernote に合わせ、初期表示 (ホバー前) を 2×2 にする
  highlight(1, 1);
  return el;
};

const isHttpUrl = (href: string): boolean => {
  try {
    return ['http:', 'https:'].includes(new URL(href, window.location.href).protocol);
  } catch {
    return false;
  }
};

/**
 * リンク挿入・編集のポップアップ
 * editor には触らず、確定時に入力値を onApply へ渡すだけ (どのコマンドで反映するかは呼び出し側)
 */
export const createLinkPopup = ({
  initialText,
  initialHref,
  onApply,
  onRemove,
}: {
  initialText: string;
  initialHref: string;
  /** href が未入力または 'https://' のままなら呼ばれない */
  onApply(link: { href: string; text: string }): void;
  onRemove(): void;
}): {
  el: HTMLElement;
  /** DOM に載せた後に呼ぶ。既存リンクの編集なら URL 欄、新規なら表示テキスト欄へ */
  focusInitialInput(): void;
} => {
  const el = document.createElement('div');
  const form = document.createElement('div');
  form.className = 'codmon-rte__link-form';
  form.innerHTML =
    '<label class="codmon-rte__link-label">表示するテキスト</label>' +
    '<input type="text" class="codmon-rte__link-input" data-name="text" placeholder="リンク文字">' +
    '<label class="codmon-rte__link-label">URL</label>' +
    '<div class="codmon-rte__link-url-row">' +
    '<input type="text" class="codmon-rte__link-input" data-name="url" placeholder="https://">' +
    '<button type="button" class="codmon-rte__link-open" aria-label="URL を新しいタブで開く" title="URL を新しいタブで開く">' +
    `<span class="codmon-rte__icon">${icon('openInNew')}</span>` +
    '</button>' +
    '</div>';
  el.appendChild(form);

  const actions = document.createElement('div');
  actions.className = 'codmon-rte__popup-actions';
  const remove = document.createElement('button');
  remove.type = 'button';
  remove.className = 'codmon-rte__popup-remove';
  remove.textContent = '解除';
  const apply = document.createElement('button');
  apply.type = 'button';
  apply.className = 'codmon-rte__popup-apply';
  apply.textContent = '適用';
  actions.appendChild(remove);
  actions.appendChild(apply);
  el.appendChild(actions);

  const textInput = form.querySelector<HTMLInputElement>('[data-name="text"]') as HTMLInputElement;
  const urlInput = form.querySelector<HTMLInputElement>('[data-name="url"]') as HTMLInputElement;
  const openButton = form.querySelector<HTMLButtonElement>('.codmon-rte__link-open') as HTMLButtonElement;
  textInput.value = initialText;
  urlInput.value = initialHref;

  openButton.addEventListener('click', () => {
    const href = urlInput.value.trim();
    if (!isHttpUrl(href)) return;
    window.open(href, '_blank', 'noopener');
  });

  const applyLink = (): void => {
    const href = urlInput.value.trim();
    if (!href || href === 'https://') return;
    onApply({ href, text: textInput.value || href });
  };
  apply.addEventListener('click', applyLink);
  remove.addEventListener('click', onRemove);

  let isComposing = false;
  urlInput.addEventListener('compositionstart', () => {
    isComposing = true;
  });
  urlInput.addEventListener('compositionend', () => {
    isComposing = false;
  });
  urlInput.addEventListener('keydown', (e) => {
    if (!shouldApplyLinkOnEnter(e, isComposing)) return;
    e.preventDefault();
    applyLink();
  });

  return {
    el,
    focusInitialInput: () => (initialText ? urlInput : textInput).focus(),
  };
};
